import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { HRMS_API_URL } from '../../config';
import ApplyFormModal from '../../comp/ApplyFormModal/ApplyFormModal';
import { FaLocationDot, FaTag } from "react-icons/fa6";
import { MdWork, MdArrowBack } from "react-icons/md";
import './JobDetailsPage.css';

export default function JobDetailsPage({ title }) {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    document.title = title || "Job Details | Gravit Infosystems";
  }, [title]);

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${HRMS_API_URL}/jobs/${id}`);
        if (response.data && response.data.success && response.data.job) {
          setJob(response.data.job);
        } else if (response.data && response.data.data) {
          setJob(response.data.data);
        } else {
          setError("Unable to load job details. Please try again later.");
        }
      } catch (err) {
        console.error("Fetch Job Details Error:", err);
        setError("Unable to load job details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchJobDetails();
    }
  }, [id]);

  const handleToggle = () => {
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';
  };

  const handleClose = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'visible';
    document.body.style.height = 'auto';
  };

  if (loading) {
    return (
      <div className="job-details__loading-container">
        <div className="job-details__loading-spinner"></div>
        <p>Loading job details...</p>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="job-details__error-container">
        <h2>Error</h2>
        <p className="job-details__error-msg">{error || "Job not found."}</p>
        <Link to="/career" className="job-details__back-link">
          <MdArrowBack /> Back to Careers
        </Link>
      </div>
    );
  }

  // Handle formats where requirements is a string or array
  const requirementsList = Array.isArray(job.requirements)
    ? job.requirements
    : (typeof job.requirements === 'string' && job.requirements.trim() !== '')
      ? job.requirements.split('\n').filter(r => r.trim() !== '')
      : [];

  return (
    <div className="job-details__container">
      <div className="job-details__header-banner">
        <div className="job-details__header-content">
          <Link to="/career" className="job-details__back-button">
            <MdArrowBack /> Back to Careers
          </Link>
          <h1 className="job-details__title">{job.title}</h1>
          <div className="job-details__meta-list">
            <span className="job-details__meta-item">
              <FaTag /> {job.category || "IT"}
            </span>
            <span className="job-details__meta-item">
              <FaLocationDot /> {job.location || "Remote"}
            </span>
            <span className="job-details__meta-item">
              <MdWork /> {job.employmentType || job.type || "Full Time"}
            </span>
          </div>
        </div>
      </div>

      <div className="job-details__content-section">
        <div className="job-details__main-info">
          {job.description && (
            <div className="job-details__info-block">
              <h3>Job Description</h3>
              <div className="job-details__description-text" dangerouslySetInnerHTML={{ __html: job.description }} />
            </div>
          )}

          {requirementsList.length > 0 && (
            <div className="job-details__info-block">
              <h3>Requirements & Qualifications</h3>
              <ul className="job-details__requirements-list">
                {requirementsList.map((req, idx) => (
                  <li key={idx}>{req}</li>
                ))}
              </ul>
            </div>
          )}

          {job.skills && (
            <div className="job-details__info-block">
              <h3>Preferred Skills</h3>
              <p>{Array.isArray(job.skills) ? job.skills.join(', ') : job.skills}</p>
            </div>
          )}
        </div>

        <div className="job-details__sidebar">
          <div className="job-details__summary-card">
            <h3>Job Summary</h3>
            <div className="job-details__summary-rows">
              <div className="job-details__summary-row">
                <span className="label">Category:</span>
                <span className="value">{job.category || "IT"}</span>
              </div>
              <div className="job-details__summary-row">
                <span className="label">Location:</span>
                <span className="value">{job.location || "Remote"}</span>
              </div>
              <div className="job-details__summary-row">
                <span className="label">Job Type:</span>
                <span className="value">{job.employmentType || job.type || "Full Time"}</span>
              </div>
              {job.experience && (
                <div className="job-details__summary-row">
                  <span className="label">Experience:</span>
                  <span className="value">{job.experience}</span>
                </div>
              )}
              {job.salary !== undefined && job.salary !== null && String(job.salary).trim() !== '' && (
                <div className="job-details__summary-row">
                  <span className="label">Salary:</span>
                  <span className="value">{job.salary}</span>
                </div>
              )}
            </div>
            <button className="job-details__apply-button" onClick={handleToggle}>
              Apply Now
            </button>
          </div>
        </div>
      </div>

      <ApplyFormModal 
        jobId={job.id || job.job_id || job._id} 
        jobTitle={job.title} 
        isOpen={isModalOpen} 
        onClose={handleClose} 
      />
    </div>
  );
}
